'use client';
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { forwardRef, Fragment, useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState, } from 'react';
import { cx } from '../../../internal/cx.js';
import { ChevronDownGlyph, ChevronRightGlyph, ColumnsGlyph, MoreGlyph, } from '../../../internal/icons.js';
import { Button, IconButton } from '../../actions/button/index.js';
import { Checkbox } from '../../forms/checkbox/index.js';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, } from '../../overlays/dropdown-menu/index.js';
import { Popover, PopoverContent, PopoverTrigger } from '../../overlays/popover/index.js';
import { EmptyState } from '../../feedback/empty-state/index.js';
import { ErrorState } from '../../feedback/error-state/index.js';
import { LoadingState } from '../../feedback/loading-state/index.js';
import { Pagination } from '../../navigation/pagination/index.js';
import { Table, TableBody, TableCaption, TableHeader, TableRow } from './data-table.js';
// Leading utility columns (expand, select) are 2.5rem wide; the pinned first
// data column starts after them.
const UTILITY_WIDTH = 2.5;
export const defaultDataTableMessages = {
    selectAll: 'Select all rows on this page',
    selectRow: (row) => `Select ${row}`,
    rowActions: (row) => `Actions for ${row}`,
    inlineAction: (action, row) => `${action} ${row}`,
    actionsHeader: 'Actions',
    expandRow: (row) => `Details for ${row}`,
    expandHeader: 'Details',
    openRow: (row) => `Open ${row}`,
    sortState: (direction, priority) => direction === 'none'
        ? ', not sorted'
        : `, sorted ${direction}${priority && priority > 1 ? `, sort ${priority}` : ''}`,
    resizeColumn: (column) => `Resize ${column} column`,
    loading: 'Loading rows',
    emptyTitle: 'Nothing here yet',
    emptyDescription: 'Rows appear here once they are added.',
    filteredTitle: 'No rows match these filters',
    filteredDescription: 'Try removing a filter or searching for something else.',
    errorTitle: 'These rows could not be loaded',
    pageSelected: (n) => `All ${n} rows on this page are selected.`,
    selectAllMatching: (total) => `Select all ${total}`,
    allMatchingSelected: (total) => `All ${total} rows are selected.`,
    clearSelection: 'Clear selection',
    pagination: 'Table pages',
};
/* ---------------------------------------------------------------- helpers */
const labelOf = (c) => c.label ?? (typeof c.header === 'string' ? c.header : c.id);
const valueOf = (c, row) => c.accessor ? c.accessor(row) : row[c.id];
function compareValues(a, b) {
    const empty = (v) => v === null || v === undefined || v === '';
    if (empty(a) || empty(b))
        return empty(a) === empty(b) ? 0 : empty(a) ? 1 : -1;
    if (a instanceof Date && b instanceof Date)
        return a.getTime() - b.getTime();
    if (typeof a === 'number' && typeof b === 'number')
        return a - b;
    if (typeof a === 'boolean' && typeof b === 'boolean')
        return Number(a) - Number(b);
    return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
}
/**
 * Sorts rows by an ordered sort list, the way DataTable does client-side.
 * Stable; empty values sort last in both directions.
 */
export function sortRows(rows, sort, columns) {
    const keys = sort
        .map((s) => ({ s, c: columns.find((c) => c.id === s.id) }))
        .filter((k) => !!k.c);
    if (!keys.length)
        return rows.slice();
    return rows
        .map((row, i) => ({ row, i }))
        .sort((x, y) => {
        for (const { s, c } of keys) {
            const ax = valueOf(c, x.row);
            const ay = valueOf(c, y.row);
            const emptyX = ax === null || ax === undefined || ax === '';
            const emptyY = ay === null || ay === undefined || ay === '';
            if (!c.sortFn && (emptyX || emptyY)) {
                if (emptyX !== emptyY)
                    return emptyX ? 1 : -1;
                continue;
            }
            const r = c.sortFn ? c.sortFn(x.row, y.row) : compareValues(ax, ay);
            if (r !== 0)
                return s.direction === 'ascending' ? r : -r;
        }
        return x.i - y.i;
    })
        .map((x) => x.row);
}
/** Next sort after pressing a header: ascending → descending → off. */
export function nextSort(current, id, additive = false) {
    const existing = current.find((s) => s.id === id);
    const next = !existing
        ? { id, direction: 'ascending' }
        : existing.direction === 'ascending'
            ? { id, direction: 'descending' }
            : null;
    if (!additive)
        return next ? [next] : [];
    const rest = current.filter((s) => s.id !== id);
    if (!next)
        return rest;
    return existing ? current.map((s) => (s.id === id ? next : s)) : [...rest, next];
}
function useControllable(value, defaultValue, onChange) {
    const [inner, setInner] = useState(defaultValue);
    const controlled = value !== undefined;
    const current = controlled ? value : inner;
    const set = useCallback((v) => {
        if (!controlled)
            setInner(v);
        onChange?.(v);
    }, [controlled, onChange]);
    return [current, set];
}
/** Width of an element, tracked with ResizeObserver (undefined until measured or on the server). */
function useWidth(ref, enabled) {
    const [width, setWidth] = useState();
    useLayoutEffectSafe(() => {
        const el = ref.current;
        if (!enabled || !el || typeof ResizeObserver === 'undefined')
            return;
        const ro = new ResizeObserver((entries) => {
            const w = entries[0]?.contentRect.width;
            if (w !== undefined)
                setWidth(Math.round(w));
        });
        ro.observe(el);
        setWidth(Math.round(el.getBoundingClientRect().width) || undefined);
        return () => ro.disconnect();
    }, [enabled]);
    return width;
}
const useLayoutEffectSafe = typeof window === 'undefined' ? useEffect : useLayoutEffect;
/* ----------------------------------------------------- grid keyboard model */
const FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]):not([type="hidden"]),select:not([disabled]),textarea:not([disabled]),[contenteditable="true"],[role="separator"][aria-valuenow]';
const TEXT_ENTRY = 'input:not([type="checkbox"]):not([type="radio"]),textarea,select,[contenteditable="true"]';
function widgetsOf(cell) {
    return Array.from(cell.querySelectorAll(FOCUSABLE)).filter((el) => !el.closest('[aria-hidden="true"]') &&
        !el.hasAttribute('data-mn-grid-skip') &&
        el.closest('td,th') === cell);
}
/** Where focus goes for a cell: its only widget, or the cell itself. */
function targetOf(cell) {
    const w = widgetsOf(cell);
    const only = w.length === 1 ? w[0] : undefined;
    return only ?? cell;
}
function gridRows(table) {
    return Array.from(table.rows).filter((r) => r.closest('table') === table);
}
/* -------------------------------------------------------------- component */
function DataTableInner(props, ref) {
    const { columns, rows, getRowId, getRowLabel, caption, showCaption = false, density = 'comfortable', stickyHeader = false, stickyFirstColumn = false, maxHeight, sort: sortProp, defaultSort = [], onSortChange, multiSort = false, manualSorting = false, selectable = false, selectedRowIds, defaultSelectedRowIds = [], onSelectedRowIdsChange, isRowSelectable, totalRowCount, onSelectAllMatching, allMatchingSelected = false, columnVisibility: visibilityProp, defaultColumnVisibility = {}, columnOrder, defaultColumnOrder, columnWidths: widthsProp, defaultColumnWidths = {}, onColumnWidthsChange, pageSize, page: pageProp, defaultPage = 1, onPageChange, manualPagination = false, loading = false, error, onRetry, filtered = false, onClearFilters, emptyState, rowActions, onRowOpen, renderExpanded, isRowExpandable, expandedRowIds, defaultExpandedRowIds = [], onExpandedRowIdsChange, keyboardNavigation, layout = 'table', cardBreakpoint = 640, footer, messages: messagesProp, className, ...rest } = props;
    const m = useMemo(() => ({ ...defaultDataTableMessages, ...messagesProp }), [messagesProp]);
    const uid = useId();
    const rootRef = useRef(null);
    const tableRef = useRef(null);
    const setRoot = useCallback((el) => {
        rootRef.current = el;
        if (typeof ref === 'function')
            ref(el);
        else if (ref)
            ref.current = el;
    }, [ref]);
    const needsWidth = layout === 'auto' || columns.some((c) => c.hideBelow !== undefined);
    const width = useWidth(rootRef, needsWidth);
    const cards = layout === 'cards' || (layout === 'auto' && width !== undefined && width < cardBreakpoint);
    const [sort, setSort] = useControllable(sortProp, defaultSort, onSortChange);
    const [selected, setSelected] = useControllable(selectedRowIds, defaultSelectedRowIds, onSelectedRowIdsChange);
    const [expanded, setExpanded] = useControllable(expandedRowIds, defaultExpandedRowIds, onExpandedRowIdsChange);
    const [widths, setWidths] = useControllable(widthsProp, defaultColumnWidths, onColumnWidthsChange);
    const [page, setPage] = useControllable(pageProp, defaultPage, onPageChange);
    const visibility = visibilityProp ?? defaultColumnVisibility;
    const order = columnOrder ?? defaultColumnOrder;
    // Columns in display order, filtered by visibility and responsive priority.
    const visibleColumns = useMemo(() => {
        let ordered = columns.slice();
        if (order?.length) {
            const pos = new Map(order.map((id, i) => [id, i]));
            ordered = ordered
                .map((c, i) => ({ c, i }))
                .sort((a, b) => (pos.get(a.c.id) ?? order.length + a.i) - (pos.get(b.c.id) ?? order.length + b.i))
                .map((x) => x.c);
        }
        return ordered.filter((c) => {
            const shown = c.hideable === false ? true : (visibility[c.id] ?? !c.defaultHidden);
            if (!shown)
                return false;
            if (!cards && c.hideBelow !== undefined && width !== undefined && width < c.hideBelow)
                return false;
            return true;
        });
    }, [columns, order, visibility, cards, width]);
    const sorted = useMemo(() => (manualSorting || !sort.length ? rows.slice() : sortRows(rows, sort, columns)), [rows, sort, columns, manualSorting]);
    const total = manualPagination ? (totalRowCount ?? rows.length) : sorted.length;
    const pageCount = pageSize ? Math.max(1, Math.ceil(total / pageSize)) : 1;
    const currentPage = Math.min(Math.max(1, page), pageCount);
    const pageRows = useMemo(() => pageSize && !manualPagination
        ? sorted.slice((currentPage - 1) * pageSize, currentPage * pageSize)
        : sorted, [sorted, pageSize, manualPagination, currentPage]);
    const rowId = (row) => getRowId(row, rows.indexOf(row));
    const rowLabel = (row) => getRowLabel?.(row) ?? rowId(row);
    const selectedSet = useMemo(() => new Set(selected), [selected]);
    const expandedSet = useMemo(() => new Set(expanded), [expanded]);
    const canSelect = (row) => isRowSelectable?.(row) ?? true;
    const pageSelectableIds = pageRows.filter(canSelect).map(rowId);
    const pageSelectedCount = pageSelectableIds.filter((id) => selectedSet.has(id)).length;
    const allPageSelected = pageSelectableIds.length > 0 && pageSelectedCount === pageSelectableIds.length;
    const lastClicked = useRef(null);
    const shiftRef = useRef(false);
    const toggleRow = (row, index, on) => {
        const id = rowId(row);
        const next = new Set(selected);
        if (shiftRef.current && lastClicked.current !== null) {
            const [a, b] = [lastClicked.current, index].sort((x, y) => x - y);
            for (const r of pageRows.slice(a, b + 1)) {
                if (!canSelect(r))
                    continue;
                if (on)
                    next.add(rowId(r));
                else
                    next.delete(rowId(r));
            }
        }
        else if (on)
            next.add(id);
        else
            next.delete(id);
        shiftRef.current = false;
        lastClicked.current = index;
        setSelected([...next]);
    };
    const togglePage = (on) => {
        const next = new Set(selected);
        for (const id of pageSelectableIds) {
            if (on)
                next.add(id);
            else
                next.delete(id);
        }
        setSelected([...next]);
    };
    const toggleExpanded = (id) => {
        const next = new Set(expanded);
        if (next.has(id))
            next.delete(id);
        else
            next.add(id);
        setExpanded([...next]);
    };
    const pressSort = (c, additive) => setSort(nextSort(sort, c.id, multiSort && additive));
    const expandable = !!renderExpanded;
    const hasActions = !!rowActions;
    const grid = (keyboardNavigation ?? !!onRowOpen) && !cards;
    const resizableAny = visibleColumns.some((c) => c.resizable);
    const widthOf = (c) => widths[c.id] ?? c.width ?? (resizableAny ? 160 : undefined);
    const leading = (expandable ? 1 : 0) + (selectable ? 1 : 0);
    const colCount = leading + visibleColumns.length + (hasActions ? 1 : 0);
    const rowById = new Map(pageRows.map((r) => [rowId(r), r]));
    /* ------------------------------------------------ grid keyboard support */
    const [active, setActive] = useState({ r: 0, c: 0 });
    const entered = useRef(false);
    useLayoutEffectSafe(() => {
        const table = tableRef.current;
        if (!table)
            return;
        const rowsEl = gridRows(table);
        if (!grid)
            return;
        const r = Math.min(active.r, rowsEl.length - 1);
        rowsEl.forEach((tr, ri) => {
            Array.from(tr.cells).forEach((cell, ci) => {
                const isActive = ri === r && ci === Math.min(active.c, tr.cells.length - 1);
                const target = targetOf(cell);
                cell.setAttribute('data-mn-cell', '');
                for (const w of widgetsOf(cell)) {
                    w.tabIndex = -1;
                    w.setAttribute('data-mn-rove', '');
                }
                if (target === cell)
                    cell.tabIndex = isActive ? 0 : -1;
                else {
                    cell.removeAttribute('tabindex');
                    target.tabIndex = isActive ? 0 : -1;
                }
            });
        });
        return () => {
            table.querySelectorAll('[data-mn-rove]').forEach((el) => {
                el.removeAttribute('tabindex');
                el.removeAttribute('data-mn-rove');
            });
            table.querySelectorAll('[data-mn-cell]').forEach((el) => {
                el.removeAttribute('tabindex');
                el.removeAttribute('data-mn-cell');
            });
        };
    });
    const focusCell = (r, c) => {
        const table = tableRef.current;
        if (!table)
            return;
        const rowsEl = gridRows(table);
        const rr = Math.max(0, Math.min(r, rowsEl.length - 1));
        const tr = rowsEl[rr];
        if (!tr)
            return;
        const cc = Math.max(0, Math.min(c, tr.cells.length - 1));
        const cell = tr.cells[cc];
        if (!cell)
            return;
        entered.current = false;
        setActive({ r: rr, c: cc });
        const target = targetOf(cell);
        target.tabIndex = 0;
        target.focus();
    };
    const resizeBy = (c, delta) => {
        const w = widthOf(c) ?? 160;
        const next = Math.min(c.maxWidth ?? 640, Math.max(c.minWidth ?? 64, w + delta));
        setWidths({ ...widths, [c.id]: next });
    };
    const onGridKeyDown = (e) => {
        const table = tableRef.current;
        const t = e.target;
        const cell = t.closest('td,th');
        if (!grid || !table || !cell || cell.closest('table') !== table)
            return;
        const rowsEl = gridRows(table);
        const tr = cell.parentElement;
        const r = rowsEl.indexOf(tr);
        const c = Array.from(tr.cells).indexOf(cell);
        const onCell = t === cell;
        const widgets = widgetsOf(cell);
        const insideMulti = !onCell && widgets.length > 1;
        if (insideMulti) {
            if (e.key === 'Escape') {
                e.preventDefault();
                cell.tabIndex = 0;
                cell.focus();
                entered.current = false;
            }
            else if (e.key === 'Tab') {
                const i = widgets.indexOf(t);
                if (i === -1)
                    return;
                e.preventDefault();
                widgets[(i + (e.shiftKey ? -1 : 1) + widgets.length) % widgets.length]?.focus();
            }
            return;
        }
        if (!onCell && t.matches(TEXT_ENTRY))
            return;
        if (!onCell && t.getAttribute('role') === 'separator')
            return;
        const columnOfCell = () => {
            const id = cell.getAttribute('data-column-id');
            return visibleColumns.find((col) => col.id === id);
        };
        if (e.altKey && (e.key === 'ArrowLeft' || e.key === 'ArrowRight') && cell.tagName === 'TH') {
            const col = columnOfCell();
            if (col?.resizable) {
                e.preventDefault();
                const dir = getComputedStyle(table).direction === 'rtl' ? -1 : 1;
                resizeBy(col, (e.key === 'ArrowRight' ? 16 : -16) * dir * (e.shiftKey ? 4 : 1));
            }
            return;
        }
        const rtl = getComputedStyle(table).direction === 'rtl';
        const lastRow = rowsEl.length - 1;
        switch (e.key) {
            case 'ArrowRight':
                e.preventDefault();
                return focusCell(r, c + (rtl ? -1 : 1));
            case 'ArrowLeft':
                e.preventDefault();
                return focusCell(r, c + (rtl ? 1 : -1));
            case 'ArrowDown':
                e.preventDefault();
                return focusCell(r + 1, c);
            case 'ArrowUp':
                e.preventDefault();
                return focusCell(r - 1, c);
            case 'Home':
                e.preventDefault();
                return e.ctrlKey || e.metaKey ? focusCell(0, 0) : focusCell(r, 0);
            case 'End':
                e.preventDefault();
                return e.ctrlKey || e.metaKey
                    ? focusCell(lastRow, Number.MAX_SAFE_INTEGER)
                    : focusCell(r, Number.MAX_SAFE_INTEGER);
            case 'PageDown':
                e.preventDefault();
                return focusCell(r + 5, c);
            case 'PageUp':
                e.preventDefault();
                return focusCell(r - 5, c);
            case 'Enter':
            case 'F2':
                if (!onCell)
                    return;
                if (widgets.length > 1) {
                    e.preventDefault();
                    entered.current = true;
                    widgets[0]?.focus();
                    return;
                }
                if (e.key === 'Enter' && onRowOpen) {
                    const row = rowById.get(tr.getAttribute('data-row-id') ?? '');
                    if (row !== undefined) {
                        e.preventDefault();
                        onRowOpen(row);
                    }
                }
                return;
            case ' ': {
                if (!onCell || !selectable)
                    return;
                const row = rowById.get(tr.getAttribute('data-row-id') ?? '');
                if (row === undefined || !canSelect(row))
                    return;
                e.preventDefault();
                toggleRow(row, pageRows.indexOf(row), !selectedSet.has(rowId(row)));
                return;
            }
        }
    };
    const onGridFocus = (e) => {
        const table = tableRef.current;
        if (!grid || !table)
            return;
        const cell = e.target.closest('td,th');
        if (!cell || cell.closest('table') !== table)
            return;
        const tr = cell.parentElement;
        const r = gridRows(table).indexOf(tr);
        const c = Array.from(tr.cells).indexOf(cell);
        if (r !== active.r || c !== active.c)
            setActive({ r, c });
    };
    /* ------------------------------------------------------------ rendering */
    const openFromClick = (e, row) => {
        if (!onRowOpen)
            return;
        const el = e.target;
        if (el.closest('a,button,input,select,textarea,label,[role="menuitem"],[role="separator"]'))
            return;
        if (typeof window !== 'undefined' && window.getSelection?.()?.toString())
            return;
        onRowOpen(row);
    };
    const pinStyle = (offsetRem) => stickyFirstColumn ? { insetInlineStart: `${offsetRem}rem` } : undefined;
    const actionsFor = (row) => {
        const list = rowActions?.(row) ?? [];
        const inline = list.filter((a) => a.inline);
        const menu = list.filter((a) => !a.inline);
        const label = rowLabel(row);
        return (_jsxs("div", { className: "mn-data-table__actions", children: [inline.map((a) => (_jsx(Button, { size: "xs", variant: "ghost", leadingIcon: a.icon, disabled: a.disabled, "data-": true, ...(a.tone ? { tone: a.tone } : {}), "aria-label": m.inlineAction(a.label, label), onClick: a.onSelect, children: a.label }, a.id))), menu.length ? (_jsxs(DropdownMenu, { children: [_jsx(DropdownMenuTrigger, { asChild: true, children: _jsx(IconButton, { size: "xs", label: m.rowActions(label), icon: _jsx(MoreGlyph, {}) }) }), _jsx(DropdownMenuContent, { align: "end", children: menu.map((a) => (_jsx(DropdownMenuItem, { icon: a.icon, ...(a.tone ? { tone: a.tone } : {}), disabled: a.disabled, onSelect: a.onSelect, children: a.label }, a.id))) })] })) : null] }));
    };
    const expandButton = (row, id) => {
        if (isRowExpandable && !isRowExpandable(row))
            return null;
        const open = expandedSet.has(id);
        return (_jsx("button", { type: "button", className: "mn-data-table__expand", "aria-expanded": open, "aria-controls": open ? `${uid}-detail-${id}` : undefined, "aria-label": m.expandRow(rowLabel(row)), "data-state": open ? 'open' : 'closed', onClick: () => toggleExpanded(id), children: _jsx(ChevronRightGlyph, {}) }));
    };
    const rowCheckbox = (row, index, id) => (_jsx(Checkbox, { "aria-label": m.selectRow(rowLabel(row)), checked: selectedSet.has(id), disabled: !canSelect(row), onClick: (e) => {
            shiftRef.current = e.shiftKey;
        }, onCheckedChange: (on) => toggleRow(row, index, on === true) }));
    const emptyBody = () => {
        if (error && !pageRows.length)
            return (_jsx(ErrorState, { size: "compact", headingLevel: "p", title: m.errorTitle, description: typeof error === 'boolean' ? undefined : error, ...(onRetry ? { onRetry } : {}) }));
        if (loading && !pageRows.length)
            return _jsx(LoadingState, { label: m.loading, variant: "rows", rows: Math.min(pageSize ?? 5, 5) });
        if (!pageRows.length)
            return (emptyState ??
                (filtered ? (_jsx(EmptyState, { size: "compact", headingLevel: "p", kind: "filtered", title: m.filteredTitle, description: m.filteredDescription, ...(onClearFilters ? { onClearFilters } : {}) })) : (_jsx(EmptyState, { size: "compact", headingLevel: "p", title: m.emptyTitle, description: m.emptyDescription }))));
        return null;
    };
    const stateContent = emptyBody();
    const banner = selectable &&
        totalRowCount !== undefined &&
        (allMatchingSelected ||
            (allPageSelected && onSelectAllMatching && totalRowCount > pageSelectableIds.length)) ? (_jsxs("div", { className: "mn-data-table__banner", children: [_jsx("span", { children: allMatchingSelected
                    ? m.allMatchingSelected(totalRowCount)
                    : m.pageSelected(pageSelectableIds.length) }), allMatchingSelected ? (_jsx(Button, { size: "xs", variant: "ghost", onClick: () => setSelected([]), children: m.clearSelection })) : (_jsx(Button, { size: "xs", variant: "ghost", onClick: onSelectAllMatching, children: m.selectAllMatching(totalRowCount) }))] })) : null;
    const errorBanner = error && pageRows.length ? (_jsx(ErrorState, { className: "mn-data-table__error", size: "compact", layout: "line", headingLevel: "p", title: m.errorTitle, description: typeof error === 'boolean' ? undefined : error, ...(onRetry ? { onRetry } : {}) })) : null;
    const headerCell = (c, i) => {
        const s = sort.findIndex((x) => x.id === c.id);
        const dir = sort[s]?.direction ?? 'none';
        const w = widthOf(c);
        const pinned = stickyFirstColumn && i === 0;
        return (_jsxs("th", { scope: "col", "data-column-id": c.id, "data-numeric": c.numeric || undefined, "data-pinned": pinned || undefined, "data-pinned-last": pinned || undefined, style: pinStyle(leading * UTILITY_WIDTH), "aria-sort": c.sortable && s === 0 ? (dir === 'none' ? undefined : dir) : undefined, className: cx('mn-table__head', c.headerClassName), children: [_jsx("div", { className: "mn-data-table__head", children: c.sortable ? (_jsxs("button", { type: "button", className: "mn-table__sort", "data-direction": dir, onClick: (e) => pressSort(c, e.shiftKey), onKeyDown: (e) => {
                            if (e.key === 'Enter' && e.shiftKey) {
                                e.preventDefault();
                                pressSort(c, true);
                            }
                        }, children: [c.header, _jsx("span", { className: "mn-visually-hidden", children: m.sortState(dir, sort.length > 1 && s !== -1 ? s + 1 : undefined) }), _jsx(ChevronDownGlyph, { className: "mn-table__sort-icon" }), sort.length > 1 && s !== -1 ? (_jsx("span", { className: "mn-data-table__sort-priority", "aria-hidden": true, children: s + 1 })) : null] })) : (c.header) }), c.resizable ? (_jsx(ResizeHandle, { label: m.resizeColumn(labelOf(c)), value: w ?? 160, min: c.minWidth ?? 64, max: c.maxWidth ?? 640, focusable: !grid, onChange: (v) => setWidths({ ...widths, [c.id]: v }), onReset: () => {
                        setWidths(Object.fromEntries(Object.entries(widths).filter(([k]) => k !== c.id)));
                    } })) : null] }, c.id));
    };
    const renderTable = () => (_jsxs(Table, { ref: tableRef, role: grid ? 'grid' : undefined, "aria-rowcount": grid && manualPagination && totalRowCount !== undefined ? totalRowCount + 1 : undefined, "aria-busy": loading || undefined, density: density, stickyHeader: stickyHeader, ...(maxHeight !== undefined ? { maxHeight } : {}), "data-layout": resizableAny ? 'fixed' : undefined, style: resizableAny
            ? {
                width: `calc(${visibleColumns.reduce((n, c) => n + (widthOf(c) ?? 160), 0)}px + ${(leading + (hasActions ? 1 : 0)) * UTILITY_WIDTH}rem)`,
            }
            : undefined, onKeyDown: grid ? onGridKeyDown : undefined, onFocus: grid ? onGridFocus : undefined, children: [_jsx(TableCaption, { className: showCaption ? undefined : 'mn-visually-hidden', children: caption }), resizableAny || visibleColumns.some((c) => c.width) ? (_jsxs("colgroup", { children: [expandable ? _jsx("col", { className: "mn-data-table__col-utility" }) : null, selectable ? _jsx("col", { className: "mn-data-table__col-utility" }) : null, visibleColumns.map((c) => {
                        const w = widthOf(c);
                        return _jsx("col", { style: w ? { width: `${w}px` } : undefined }, c.id);
                    }), hasActions ? _jsx("col", {}) : null] })) : null, _jsx(TableHeader, { children: _jsxs(TableRow, { children: [expandable ? (_jsx("th", { scope: "col", className: "mn-table__head mn-data-table__utility", "data-pinned": stickyFirstColumn || undefined, style: pinStyle(0), children: _jsx("span", { className: "mn-visually-hidden", children: m.expandHeader }) })) : null, selectable ? (_jsx("th", { scope: "col", className: "mn-table__head mn-table__select mn-data-table__utility", "data-pinned": stickyFirstColumn || undefined, style: pinStyle(expandable ? UTILITY_WIDTH : 0), children: _jsx(Checkbox, { "aria-label": m.selectAll, disabled: pageSelectableIds.length === 0, checked: allPageSelected ? true : pageSelectedCount > 0 ? 'indeterminate' : false, onCheckedChange: (on) => togglePage(on === true) }) })) : null, visibleColumns.map(headerCell), hasActions ? (_jsx("th", { scope: "col", className: "mn-table__head mn-data-table__actions-head", children: _jsx("span", { className: "mn-visually-hidden", children: m.actionsHeader }) })) : null] }) }), _jsx(TableBody, { children: stateContent !== null ? (_jsx("tr", { className: "mn-data-table__state-row", children: _jsx("td", { colSpan: colCount, className: "mn-table__cell mn-data-table__state", children: stateContent }) })) : (pageRows.map((row, index) => {
                    const id = rowId(row);
                    const isSelected = selectedSet.has(id);
                    const isOpen = expandable && expandedSet.has(id);
                    return (_jsxs(Fragment, { children: [_jsxs(TableRow, { "data-row-id": id, ...(selectable ? { selected: isSelected } : {}), interactive: !!onRowOpen, "data-expanded": isOpen || undefined, onClick: onRowOpen ? (e) => openFromClick(e, row) : undefined, children: [expandable ? (_jsx("td", { className: "mn-table__cell mn-data-table__utility", "data-pinned": stickyFirstColumn || undefined, style: pinStyle(0), children: expandButton(row, id) })) : null, selectable ? (_jsx("td", { className: "mn-table__cell mn-table__select mn-data-table__utility", "data-pinned": stickyFirstColumn || undefined, style: pinStyle(expandable ? UTILITY_WIDTH : 0), children: rowCheckbox(row, index, id) })) : null, visibleColumns.map((c, ci) => {
                                        const content = c.cell ? c.cell(row, index) : formatValue(valueOf(c, row));
                                        const header = c.rowHeader ?? ci === 0;
                                        const pinned = stickyFirstColumn && ci === 0;
                                        const shared = {
                                            'data-column-id': c.id,
                                            'data-numeric': c.numeric || undefined,
                                            'data-pinned': pinned || undefined,
                                            'data-pinned-last': pinned || undefined,
                                            style: pinned ? pinStyle(leading * UTILITY_WIDTH) : undefined,
                                            className: cx('mn-table__cell', header && 'mn-data-table__row-head', c.className),
                                        };
                                        return header ? (_jsx("th", { scope: "row", ...shared, children: content }, c.id)) : (_jsx("td", { role: grid ? 'gridcell' : undefined, ...shared, children: content }, c.id));
                                    }), hasActions ? (_jsx("td", { className: "mn-table__cell mn-data-table__actions-cell", children: actionsFor(row) })) : null] }), isOpen ? (_jsx("tr", { className: "mn-data-table__detail", id: `${uid}-detail-${id}`, children: _jsx("td", { colSpan: colCount, className: "mn-table__cell", children: renderExpanded?.(row) }) })) : null] }, id));
                })) })] }));
    const renderCards = () => {
        if (stateContent !== null)
            return _jsx("div", { className: "mn-data-table__state", children: stateContent });
        const [first, ...others] = visibleColumns;
        return (_jsx("ul", { className: "mn-data-table__cards", "aria-label": caption, "aria-busy": loading || undefined, children: pageRows.map((row, index) => {
                const id = rowId(row);
                const isSelected = selectedSet.has(id);
                const isOpen = expandable && expandedSet.has(id);
                return (_jsxs("li", { className: "mn-data-table__card", "data-selected": (selectable && isSelected) || undefined, children: [_jsxs("div", { className: "mn-data-table__card-head", children: [selectable ? rowCheckbox(row, index, id) : null, _jsx("div", { className: "mn-data-table__card-title", children: first
                                        ? first.cell
                                            ? first.cell(row, index)
                                            : formatValue(valueOf(first, row))
                                        : null }), hasActions ? actionsFor(row) : null, onRowOpen ? (_jsx(IconButton, { size: "xs", label: m.openRow(rowLabel(row)), icon: _jsx(ChevronRightGlyph, {}), onClick: () => onRowOpen(row) })) : null] }), others.length ? (_jsx("dl", { className: "mn-data-table__card-fields", children: others.map((c) => (_jsxs("div", { className: "mn-data-table__card-field", "data-numeric": c.numeric || undefined, children: [_jsx("dt", { children: labelOf(c) }), _jsx("dd", { children: c.cell ? c.cell(row, index) : formatValue(valueOf(c, row)) })] }, c.id))) })) : null, expandable && (!isRowExpandable || isRowExpandable(row)) ? (_jsxs(_Fragment, { children: [_jsxs("button", { type: "button", className: "mn-data-table__card-expand", "aria-expanded": isOpen, "aria-controls": isOpen ? `${uid}-detail-${id}` : undefined, "data-state": isOpen ? 'open' : 'closed', onClick: () => toggleExpanded(id), children: [_jsx(ChevronRightGlyph, { "aria-hidden": true }), m.expandRow(rowLabel(row))] }), isOpen ? (_jsx("div", { className: "mn-data-table__card-detail", id: `${uid}-detail-${id}`, children: renderExpanded?.(row) })) : null] })) : null] }, id));
            }) }));
    };
    return (_jsxs("div", { ref: setRoot, ...rest, className: cx('mn-data-table', className), "data-view": cards ? 'cards' : 'table', "data-busy": loading || undefined, "data-sticky-first": stickyFirstColumn || undefined, children: [_jsx("span", { className: "mn-visually-hidden", role: "status", children: loading ? m.loading : '' }), banner, errorBanner, loading && pageRows.length ? _jsx("div", { className: "mn-data-table__progress", "aria-hidden": true }) : null, cards ? (_jsxs(_Fragment, { children: [showCaption ? _jsx("p", { className: "mn-table__caption", children: caption }) : null, renderCards()] })) : (renderTable()), footer || (pageSize && pageCount > 1) ? (_jsxs("div", { className: "mn-data-table__footer", children: [footer, pageSize && pageCount > 1 ? (_jsx(Pagination, { className: "mn-data-table__pagination", page: currentPage, pageCount: pageCount, pageSize: pageSize, totalItems: total, variant: cards ? 'compact' : 'full', label: m.pagination, onPageChange: setPage })) : null] })) : null] }));
}
function formatValue(v) {
    if (v === null || v === undefined)
        return '';
    if (v instanceof Date)
        return v.toLocaleDateString();
    if (typeof v === 'object')
        return '';
    return String(v);
}
/**
 * A data table driven by column definitions: sorting (single or multi),
 * row selection with select-all-on-page, column visibility, order and
 * resizing, sticky header and first column, pagination (client or server),
 * expandable rows, row actions, built-in loading, empty and error states,
 * keyboard grid navigation and a stacked card layout for narrow screens.
 * Built on the Table parts.
 */
export const DataTable = forwardRef(DataTableInner);
function ResizeHandle({ label, value, min, max, focusable, onChange, onReset }) {
    const start = useRef(null);
    const clamp = (v) => Math.round(Math.min(max, Math.max(min, v)));
    const onPointerDown = (e) => {
        e.preventDefault();
        e.stopPropagation();
        const el = e.currentTarget;
        el.setPointerCapture?.(e.pointerId);
        start.current = {
            x: e.clientX,
            w: value,
            dir: getComputedStyle(el).direction === 'rtl' ? -1 : 1,
        };
        el.setAttribute('data-dragging', '');
    };
    const onPointerMove = (e) => {
        const s = start.current;
        if (!s)
            return;
        onChange(clamp(s.w + (e.clientX - s.x) * s.dir));
    };
    const end = (e) => {
        start.current = null;
        e.currentTarget.removeAttribute('data-dragging');
    };
    return (
    // A focusable separator is the WAI-ARIA window-splitter widget (interactive).
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
    _jsx("div", { role: "separator", "aria-orientation": "vertical", "aria-label": label, "aria-valuenow": value, "aria-valuemin": min, "aria-valuemax": max, 
        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- focusable splitter
        tabIndex: focusable ? 0 : undefined, "data-mn-grid-skip": "", className: "mn-data-table__resize", onPointerDown: onPointerDown, onPointerMove: onPointerMove, onPointerUp: end, onPointerCancel: end, onClick: (e) => e.stopPropagation(), onDoubleClick: onReset, onKeyDown: (e) => {
            const step = e.shiftKey ? 64 : 16;
            const dir = getComputedStyle(e.currentTarget).direction === 'rtl' ? -1 : 1;
            const map = {
                ArrowRight: value + step * dir,
                ArrowLeft: value - step * dir,
                Home: min,
                End: max,
            };
            const next = map[e.key];
            if (next === undefined)
                return;
            e.preventDefault();
            e.stopPropagation();
            onChange(clamp(next));
        } }));
}
export const defaultColumnChooserMessages = {
    trigger: 'Columns',
    title: 'Show columns',
    moveUp: (c) => `Move ${c} up`,
    moveDown: (c) => `Move ${c} down`,
    reset: 'Reset columns',
};
/** Column definitions → chooser entries (labels resolved the way DataTable does). */
export function columnChooserColumns(columns) {
    return columns.map((c) => ({ id: c.id, label: labelOf(c), hideable: c.hideable !== false }));
}
/**
 * Popover to show, hide and reorder DataTable columns. Reordering uses
 * buttons, so it works by keyboard and screen reader without drag and drop.
 */
export const DataTableColumnChooser = forwardRef(function DataTableColumnChooser({ columns, visibility, onVisibilityChange, order, onOrderChange, onReset, messages, size = 'sm', className, }, ref) {
    const m = { ...defaultColumnChooserMessages, ...messages };
    const titleId = useId();
    const ordered = useMemo(() => {
        if (!order?.length)
            return columns.slice();
        const pos = new Map(order.map((id, i) => [id, i]));
        return columns
            .map((c, i) => ({ c, i }))
            .sort((a, b) => (pos.get(a.c.id) ?? order.length + a.i) - (pos.get(b.c.id) ?? order.length + b.i))
            .map((x) => x.c);
    }, [columns, order]);
    const buttons = useRef(new Map());
    const refocus = useRef(null);
    useEffect(() => {
        if (!refocus.current)
            return;
        const el = buttons.current.get(refocus.current);
        refocus.current = null;
        if (el && !el.disabled)
            el.focus();
    });
    const move = (id, delta) => {
        const ids = ordered.map((c) => c.id);
        const i = ids.indexOf(id);
        const j = i + delta;
        if (j < 0 || j >= ids.length)
            return;
        const a = ids[i];
        const b = ids[j];
        if (a === undefined || b === undefined)
            return;
        ids[i] = b;
        ids[j] = a;
        const key = `${id}:${delta}`;
        const atEdge = j === 0 || j === ids.length - 1;
        refocus.current = atEdge ? `${id}:${-delta}` : key;
        onOrderChange?.(ids);
    };
    return (_jsxs(Popover, { children: [_jsx(PopoverTrigger, { asChild: true, children: _jsx(Button, { ref: ref, size: size, variant: "secondary", className: className, leadingIcon: _jsx(ColumnsGlyph, {}), children: m.trigger }) }), _jsxs(PopoverContent, { align: "end", "aria-labelledby": `${titleId}-title`, className: "mn-data-table__chooser-panel", children: [_jsx("p", { className: "mn-data-table__chooser-title", id: `${titleId}-title`, children: m.title }), _jsx("ul", { className: "mn-data-table__chooser", children: ordered.map((c, i) => (_jsxs("li", { className: "mn-data-table__chooser-item", children: [_jsx(Checkbox, { label: c.label, checked: visibility[c.id] ?? true, disabled: c.hideable === false, onCheckedChange: (on) => onVisibilityChange({ ...visibility, [c.id]: on === true }) }), onOrderChange ? (_jsxs("span", { className: "mn-data-table__chooser-move", children: [_jsx(IconButton, { ref: (el) => {
                                                buttons.current.set(`${c.id}:-1`, el);
                                            }, size: "xs", label: m.moveUp(c.label), disabled: i === 0, icon: _jsx(ChevronDownGlyph, { style: { transform: 'rotate(180deg)' } }), onClick: () => move(c.id, -1) }), _jsx(IconButton, { ref: (el) => {
                                                buttons.current.set(`${c.id}:1`, el);
                                            }, size: "xs", label: m.moveDown(c.label), disabled: i === ordered.length - 1, icon: _jsx(ChevronDownGlyph, {}), onClick: () => move(c.id, 1) })] })) : null] }, c.id))) }), onReset ? (_jsx(Button, { size: "xs", variant: "ghost", className: "mn-data-table__chooser-reset", onClick: onReset, children: m.reset })) : null] })] }));
});
/**
 * Column visibility and order state shared by a DataTable and its
 * DataTableColumnChooser. Persist `visibility`/`order` yourself if the
 * choice should survive a reload (saved views, local storage).
 */
export function useDataTableColumns(columns, initial = {}) {
    const defaults = useMemo(() => {
        const v = {};
        for (const c of columns)
            v[c.id] = c.hideable === false ? true : !c.defaultHidden;
        return v;
    }, [columns]);
    const [visibility, setVisibility] = useState(() => ({
        ...defaults,
        ...initial.visibility,
    }));
    const [order, setOrder] = useState(() => initial.order ?? columns.map((c) => c.id));
    const reset = useCallback(() => {
        setVisibility(defaults);
        setOrder(columns.map((c) => c.id));
    }, [defaults, columns]);
    const chooserColumns = useMemo(() => columnChooserColumns(columns), [columns]);
    return {
        visibility,
        order,
        setVisibility,
        setOrder,
        reset,
        tableProps: { columnVisibility: visibility, columnOrder: order },
        chooserProps: {
            columns: chooserColumns,
            visibility,
            onVisibilityChange: setVisibility,
            order,
            onOrderChange: setOrder,
            onReset: reset,
        },
    };
}
//# sourceMappingURL=data-table.grid.js.map